import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr, fmtGBP, API } from "@/lib/api";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { Pencil, FloppyDisk, X, Plus, Trash, Check, Buildings, Camera, User as UserIcon } from "@phosphor-icons/react";

const BUILTIN_ROLE_ORDER = ["admin", "general_manager", "maintenance_manager", "district_manager", "area_manager", "store_manager", "store_staff", "maintenance_staff", "contractor"];

function limitDisplay(lim, fmt) {
  if (lim === -1) return "Unlimited";
  if (lim === undefined) return "—";
  return fmt(lim);
}

function avatarLabel(busy, hasAvatar) {
  if (busy) return "Uploading…";
  return hasAvatar ? "Change" : "Upload photo";
}

export default function Users() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => http.get("/users").then(r => r.data) });
  const { data: limits = [] } = useQuery({ queryKey: ["role-limits"], queryFn: () => http.get("/role-limits").then(r => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: skills = [] } = useQuery({ queryKey: ["skills"], queryFn: () => http.get("/skills").then(r => r.data) });
  const { data: rolesData } = useQuery({ queryKey: ["roles"], queryFn: () => http.get("/roles").then(r => r.data), retry: false });
  const limitMap = Object.fromEntries(limits.map(l => [l.role, l.max_approval_amount_gbp]));
  const storeMap = Object.fromEntries(stores.map(s => [s.id, s.name]));

  // All assignable roles (built-ins first, then custom)
  const allRoles = [
    ...BUILTIN_ROLE_ORDER.map((slug) => ({ slug, label: ROLE_LABEL[slug] || slug, is_builtin: true })),
    ...((rolesData?.customs || []).map((c) => ({ slug: c.slug, label: c.label, is_builtin: false, is_technician: c.is_technician }))),
  ];
  const roleByKey = Object.fromEntries(allRoles.map(r => [r.slug, r]));
  const labelFor = (slug) => roleByKey[slug]?.label || ROLE_LABEL[slug] || slug;
  const customRoleLimitMap = Object.fromEntries((rolesData?.customs || []).map(c => [c.slug, c.max_approval_amount_gbp]));

  const canCreateUser = ["admin", "general_manager"].includes(me?.role);
  const canEditUser = canCreateUser;
  const canDeleteUser = me?.role === "admin";
  const canAssignStores = canCreateUser;
  const canEditLimits = me?.role === "admin";

  // role-limit edit
  const [editRole, setEditRole] = useState(null);
  const [editVal, setEditVal] = useState("");

  // user modal (create + edit)
  const [userModal, setUserModal] = useState(null); // {mode, id, data}
  const [uf, setUf] = useState(blankUser());
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  function blankUser() {
    return { name: "", email: "", password: "", role: "store_staff", store_id: "", district: "", area: "", managed_store_ids: [], skill_ids: [], phone: "", address: "", avatar_file_id: null };
  }

  const openCreate = () => { setUf(blankUser()); setUserModal({ mode: "create" }); setErr(""); };
  const openEdit = (u) => {
    setUf({
      name: u.name || "", email: u.email, password: "", role: u.role,
      store_id: u.store_id || "",
      district: u.scope?.district || "", area: u.scope?.area || "",
      managed_store_ids: u.managed_store_ids || [],
      skill_ids: u.skill_ids || [],
      phone: u.phone || "", address: u.address || "", avatar_file_id: u.avatar_file_id || null,
    });
    setUserModal({ mode: "edit", id: u.id, email: u.email });
    setErr("");
  };

  const toggleManaged = (sid) => setUf(prev => ({ ...prev, managed_store_ids: prev.managed_store_ids.includes(sid) ? prev.managed_store_ids.filter(x => x !== sid) : [...prev.managed_store_ids, sid] }));
  const toggleSkill = (sid) => setUf(prev => ({ ...prev, skill_ids: prev.skill_ids.includes(sid) ? prev.skill_ids.filter(x => x !== sid) : [...prev.skill_ids, sid] }));

  const avatarInputRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const onUploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await http.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUf((p) => ({ ...p, avatar_file_id: r.data.id }));
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail) || "Upload failed"); }
    finally { setAvatarBusy(false); if (avatarInputRef.current) avatarInputRef.current.value = ""; }
  };

  const submitUser = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = { ...uf };
      if (userModal.mode === "edit" && !body.password) delete body.password;
      if (userModal.mode === "create") await http.post("/users", body);
      else await http.patch(`/users/${userModal.id}`, body);
      qc.invalidateQueries({ queryKey: ["users"] });
      setUserModal(null);
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveLimit = async () => {
    try {
      await http.patch("/role-limits", { role: editRole, max_approval_amount_gbp: Number(editVal) });
      qc.invalidateQueries({ queryKey: ["role-limits"] });
      setEditRole(null);
    } catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  const removeUser = async (id, name) => {
    if (!window.confirm(`Soft-delete user "${name}"?`)) return;
    try { await http.delete(`/users/${id}`); qc.invalidateQueries({ queryKey: ["users"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  const isTech = roleByKey[uf.role]?.is_builtin
    ? ["maintenance_staff", "contractor"].includes(uf.role)
    : !!roleByKey[uf.role]?.is_technician;
  const isRegionalManager = ["district_manager", "area_manager"].includes(uf.role);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Access Control</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Users & Approval Limits</h1>
        </div>
        {canCreateUser && (
          <button onClick={openCreate} data-testid="users-add-button" className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
            <Plus size={14} weight="bold" /> Add user
          </button>
        )}
      </div>

      {/* Role limits */}
      <div className="rmms-card mb-6">
        <div className="px-5 py-3 border-b border-slate-200 font-heading font-semibold text-sm">Role approval limits (GBP)</div>
        <div className="divide-y divide-slate-100">
          {[...BUILTIN_ROLE_ORDER, ...(rolesData?.customs || []).map(c => c.slug)].map((role) => {
            const isCustomRole = !BUILTIN_ROLE_ORDER.includes(role);
            const lim = isCustomRole ? customRoleLimitMap[role] : limitMap[role];
            const displayVal = limitDisplay(lim, fmtGBP);
            return (
              <div key={role} data-testid={`role-limit-row-${role}`} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-5 py-3">
                <div className="text-sm font-medium flex items-center gap-2">
                  {labelFor(role)}
                  {isCustomRole && <span className="text-[10px] font-heading uppercase tracking-wider font-semibold border rounded-sm px-1.5 py-0.5 bg-blue-50 text-[#002FA7] border-blue-200">Custom</span>}
                </div>
                {!isCustomRole && editRole === role ? (
                  <input type="number" step="0.01" value={editVal} onChange={e => setEditVal(e.target.value)} data-testid={`role-limit-input-${role}`} className="rmms-input h-9 w-40 font-mono-data" autoFocus />
                ) : (
                  <span className="font-mono-data text-sm tabular-nums" data-testid={`role-limit-value-${role}`}>{displayVal}</span>
                )}
                {canEditLimits && !isCustomRole ? (
                  editRole === role ? (
                    <div className="flex gap-1">
                      <button onClick={saveLimit} data-testid={`role-limit-save-${role}`} className="h-8 w-8 inline-flex items-center justify-center text-white rounded-sm" style={{ background: "#002FA7" }}><FloppyDisk size={14} /></button>
                      <button onClick={() => setEditRole(null)} className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 rounded-sm"><X size={14} /></button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditRole(role); setEditVal(lim ?? 0); }} data-testid={`role-limit-edit-${role}`} className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 rounded-sm hover:bg-slate-50"><Pencil size={12} /></button>
                  )
                ) : <span className="text-[10px] text-slate-400">{isCustomRole ? "Edit in Roles" : ""}</span>}
              </div>
            );
          })}
        </div>
        <div className="px-5 py-2 text-xs text-slate-500 border-t border-slate-200 bg-slate-50">Use <span className="font-mono-data">-1</span> for unlimited approval. Custom role limits are managed on the Roles page.</div>
      </div>

      {/* Users */}
      <div className="rmms-card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 font-heading font-semibold text-sm">Users ({users.length})</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Role</th>
              <th className="px-4 py-2.5">Stores managed</th><th className="px-4 py-2.5">Skills</th><th className="px-4 py-2.5 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                      {u.avatar_file_id ? (
                        <img src={`${API}/files/${u.avatar_file_id}`} alt="" className="h-full w-full object-cover" data-testid={`user-avatar-thumb-${u.email}`} />
                      ) : (
                        <UserIcon size={12} className="text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate">{u.name}</div>
                      {u.phone && <div className="text-[10px] text-slate-500 font-mono-data truncate">{u.phone}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono-data text-[12px] text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><span className="text-[11px] font-heading uppercase tracking-wider font-semibold border rounded-sm px-2 py-0.5 bg-slate-50 text-slate-700 border-slate-200">{labelFor(u.role)}</span>{!roleByKey[u.role]?.is_builtin && roleByKey[u.role] && <span className="ml-1 text-[10px] font-heading uppercase tracking-wider font-semibold border rounded-sm px-1.5 py-0.5 bg-blue-50 text-[#002FA7] border-blue-200">Custom</span>}</td>
                <td className="px-4 py-3 text-[12px] text-slate-600">
                  {u.store_id && <span className="font-mono-data">{storeMap[u.store_id] || "—"}</span>}
                  {u.managed_store_ids?.length > 0 && (
                    <span className="ml-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-slate-200 rounded-sm bg-slate-50"><Buildings size={9}/>+{u.managed_store_ids.length}</span>
                  )}
                  {!u.store_id && !u.managed_store_ids?.length && (u.scope?.district || u.scope?.area || (["admin","general_manager","maintenance_manager"].includes(u.role) ? "All" : "—"))}
                </td>
                <td className="px-4 py-3 text-[12px] text-slate-600">{(u.skill_ids?.length || u.skill_tags?.length) ? <span className="font-mono-data">{(u.skill_ids?.length) || u.skill_tags.length}</span> : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    {canEditUser && <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.email}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
                    {canDeleteUser && u.id !== me?.id && <button onClick={() => removeUser(u.id, u.name)} data-testid={`user-delete-${u.email}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {userModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="user-modal">
          <form onSubmit={submitUser} className="bg-white border border-slate-200 rounded-md w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div><div className="overline">{userModal.mode === "create" ? "New" : "Edit"}</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">User</h2></div>
              <button type="button" onClick={() => setUserModal(null)} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Full name *</label>
                <input required value={uf.name} onChange={e => setUf({ ...uf, name: e.target.value })} data-testid="user-name-input" className="rmms-input" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Email {userModal.mode === "edit" && <span className="text-slate-400">(locked)</span>}</label>
                <input required type="email" disabled={userModal.mode === "edit"} value={userModal.mode === "edit" ? userModal.email : uf.email} onChange={e => setUf({ ...uf, email: e.target.value })} data-testid="user-email-input" className="rmms-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Password {userModal.mode === "edit" && <span className="text-slate-400">(leave blank to keep)</span>}</label>
                <input type="password" required={userModal.mode === "create"} value={uf.password} onChange={e => setUf({ ...uf, password: e.target.value })} data-testid="user-password-input" className="rmms-input" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Role *</label>
                <select value={uf.role} onChange={e => setUf({ ...uf, role: e.target.value })} data-testid="user-role-select" className="rmms-input">
                  {allRoles.map(r => <option key={r.slug} value={r.slug}>{r.label}{!r.is_builtin ? " (Custom)" : ""}</option>)}
                </select>
              </div>
            </div>

            {/* Profile: avatar + contact details */}
            <div className="border border-slate-200 rounded-md p-4 space-y-3 bg-slate-50/40">
              <div className="overline">Profile</div>
              <div className="flex items-start gap-4">
                <div className="shrink-0">
                  <div className="h-20 w-20 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center" data-testid="user-avatar-preview">
                    {uf.avatar_file_id ? (
                      <img src={`${API}/files/${uf.avatar_file_id}`} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <UserIcon size={28} className="text-slate-400" />
                    )}
                  </div>
                  <label className="mt-2 flex items-center justify-center gap-1 text-[11px] cursor-pointer text-[#002FA7] hover:underline">
                    <Camera size={12} />
                    {avatarLabel(avatarBusy, !!uf.avatar_file_id)}
                    <input ref={avatarInputRef} type="file" accept="image/*" onChange={onUploadAvatar} data-testid="user-avatar-input" className="hidden" />
                  </label>
                  {uf.avatar_file_id && (
                    <button type="button" onClick={() => setUf({ ...uf, avatar_file_id: null })} data-testid="user-avatar-remove" className="block mx-auto mt-1 text-[10px] text-rose-600 hover:underline">Remove</button>
                  )}
                </div>
                <div className="flex-1 grid grid-cols-1 gap-3">
                  <div>
                    <label className="overline mb-1.5 block">Phone</label>
                    <input value={uf.phone} onChange={e => setUf({ ...uf, phone: e.target.value })} data-testid="user-phone-input" className="rmms-input" placeholder="+44 ..." />
                  </div>
                  <div>
                    <label className="overline mb-1.5 block">Address</label>
                    <textarea rows={2} value={uf.address} onChange={e => setUf({ ...uf, address: e.target.value })} data-testid="user-address-input" className="rmms-input resize-none" placeholder="Street, City, Postcode" />
                  </div>
                </div>
              </div>
            </div>

            {/* Primary store for store_manager / store_staff */}
            {(uf.role === "store_manager" || uf.role === "store_staff") && (
              <div>
                <label className="overline mb-1.5 block">Primary store *</label>
                <select required value={uf.store_id} onChange={e => setUf({ ...uf, store_id: e.target.value })} data-testid="user-store-select" className="rmms-input">
                  <option value="">Select…</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            {/* Regional scope for district/area */}
            {isRegionalManager && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline mb-1.5 block">District</label><input value={uf.district} onChange={e => setUf({ ...uf, district: e.target.value })} data-testid="user-district-input" className="rmms-input" /></div>
                <div><label className="overline mb-1.5 block">Area</label><input value={uf.area} onChange={e => setUf({ ...uf, area: e.target.value })} data-testid="user-area-input" className="rmms-input" /></div>
              </div>
            )}

            {/* Multi-store managed by this user */}
            {canAssignStores && !["admin", "general_manager", "maintenance_manager", "maintenance_staff", "contractor", "store_staff"].includes(uf.role) && !roleByKey[uf.role]?.is_technician && (
              <div>
                <label className="overline mb-1.5 block flex items-center gap-2">Managed stores
                  <span className="text-[10px] text-slate-400 normal-case tracking-normal">— multi-select for area/district managers</span>
                </label>
                <div className="flex flex-wrap gap-1 p-2 border border-slate-200 rounded-sm max-h-32 overflow-y-auto">
                  {stores.map(s => (
                    <button key={s.id} type="button" onClick={() => toggleManaged(s.id)} data-testid={`user-managed-store-${s.id}`}
                      className={`text-[11px] px-2 py-1 border rounded-sm transition-colors ${
                        uf.managed_store_ids.includes(s.id) ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-200 hover:bg-slate-50"
                      }`}>
                      {uf.managed_store_ids.includes(s.id) && <Check size={10} className="inline -mt-0.5 mr-0.5" />}
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Skills for technician/contractor */}
            {isTech && (
              <div>
                <label className="overline mb-1.5 block">Skills</label>
                <div className="flex flex-wrap gap-1 p-2 border border-slate-200 rounded-sm max-h-32 overflow-y-auto">
                  {skills.map(s => (
                    <button key={s.id} type="button" onClick={() => toggleSkill(s.id)} data-testid={`user-skill-${s.id}`}
                      className={`text-[11px] px-2 py-1 border rounded-sm font-mono-data transition-colors ${
                        uf.skill_ids.includes(s.id) ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-200 hover:bg-slate-50"
                      }`}>
                      {uf.skill_ids.includes(s.id) && <Check size={10} className="inline -mt-0.5 mr-0.5" />}
                      {s.name}
                    </button>
                  ))}
                  {skills.length === 0 && <span className="text-xs text-slate-400 px-1">No skills in catalogue yet.</span>}
                </div>
              </div>
            )}

            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={() => setUserModal(null)} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="user-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
